/**
 * UnifiedTable.tsx — Category drill-down merging categories + subcategories +
 * line items, with inline discrepancy expansion. Three tabs switch the view
 * (category tree / flat all-items / discrepancies-only) and "Filtra" narrows by
 * severity. Wired to the live tender-api: categories + line items come from
 * useAuditCategories / useAuditLineItems (props), grouped by the category FK.
 */
import { Fragment, useState } from "react";

import { I } from "../../components/icons";
import { DropdownMenu } from "../../components/ui/DropdownMenu";
import {
  type AnalysisJob,
  type AuditCategory,
  type AuditLineItem,
  itemCheckState,
  itemFlags,
  type TopLineItems as TopLineItemsData,
  unreadAmountsWords,
  unreadWords,
} from "../../lib/tenderApi";
import { DiscrepancyContent } from "./DiscrepancyContent";
import { ItemBillPage, type ItemPagesView } from "./ItemBillPage";
import { CHECK_REASON_LABEL, CHECK_STATE_LABEL, CHECK_STATE_SHORT } from "./CheckSummaryPanel";
import { fmtEur, statusFromBackend } from "./helpers";
import { TopLineItems } from "./TopLineItems";
import { failureLine } from "../../lib/jobFailure";

type View = "tree" | "all" | "discrepancies" | "pareto";

const NOT_DETERMINABLE = "non determinabile";

/** Goal 55B, L-1 — a money total: the sum of the amounts READ, with the count of the ones
 * nobody read beneath it; «non determinabile» when every amount is unread. Never € 0 for an
 * unknown, and «—» only where it always meant «nothing here». */
function KnownTotal({ cost, unread }: { cost: number | null; unread: number }): JSX.Element {
  const value = cost != null ? fmtEur(cost) : unread ? NOT_DETERMINABLE : "—";
  if (!unread) return <>{value}</>;
  return (
    <>
      {value}
      <div
        data-testid="unread-amounts"
        className="muted"
        style={{ fontSize: 10.5, fontWeight: 400 }}
      >
        {unreadAmountsWords(unread)}
      </div>
    </>
  );
}
type Severity = "all" | "err" | "warn";

interface SharedRowProps {
  projectId: string;
  openRow: string | null;
  openDiscrepancy: (item: AuditLineItem) => void;
  /** G3a — the bill page of each item; absent outside the Audit page (nothing is shown). */
  itemPages?: ItemPagesView;
}

function Tab({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: "12px 16px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? "var(--c-ink)" : "var(--c-ink-3)",
        borderBottom: active ? "2px solid var(--c-brand)" : "2px solid transparent",
        marginBottom: -1,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {children}
    </button>
  );
}

/** Goal 55A: the item's check state, with its reason (or missing fields) on hover. */
function CheckStateBadge({ item }: { item: AuditLineItem }): JSX.Element {
  const { state, reason } = itemCheckState(item);
  const missing = item.missingFields ?? [];
  const unread = state === "incompleta" ? unreadWords(missing) : "";
  const detail =
    state === "non_controllata" && reason
      ? CHECK_REASON_LABEL[reason]
      : unread || CHECK_STATE_LABEL[state];
  const tone =
    state === "controllata_coerente"
      ? "pill-ok"
      : state === "controllata_con_differenze"
        ? "pill-warn"
        : state === "incompleta"
          ? "pill-err"
          : "";
  const pill = (
    <span
      data-testid="item-check-state"
      className={`pill ${tone}`}
      title={item.checkLists ? `${detail} — listini consultati: ${item.checkLists}` : detail}
      style={{ whiteSpace: "nowrap" }}
    >
      {CHECK_STATE_SHORT[state]}
    </span>
  );
  // Goal 55A, D19 (corrected 4 Oct): which field was not read — and the printed text of a
  // number that reads two ways — in plain sight beside the state, not only on hover.
  if (!unread) return pill;
  return (
    <>
      {pill}
      <span data-testid="item-unread" className="muted" style={{ fontSize: 11 }}>
        {unread}
      </span>
    </>
  );
}

function LineItemRow({
  item,
  projectId,
  openRow,
  openDiscrepancy,
  itemPages,
}: { item: AuditLineItem } & SharedRowProps): JSX.Element {
  const isOpen = openRow === item.id;
  const hasD = !!item.discrepancy;
  const status = hasD ? statusFromBackend(item.discrepancy!.status) : null;
  const sevColor = item.discrepancy?.severity === "err" ? "var(--c-err)" : "var(--c-warn)";

  return (
    <Fragment>
      <tr
        onClick={() => hasD && openDiscrepancy(item)}
        style={{
          cursor: hasD ? "pointer" : "default",
          background: isOpen ? "var(--c-warn-soft)" : undefined,
        }}
      >
        <td>{hasD && <I.Alert size={13} style={{ color: sevColor }} />}</td>
        <td className="t-mono" style={{ color: "var(--c-brand)", fontSize: 11.5, fontWeight: 500 }}>
          {item.code ?? item.id.slice(0, 8)}
        </td>
        <td style={{ paddingLeft: 24, color: "var(--c-ink)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 460,
                display: "inline-block",
              }}
            >
              {item.description}
            </span>
            <ItemBillPage itemId={item.id} pages={itemPages} />
            {status === "Risolto" && (
              <span className="pill pill-ok">
                <I.Check size={10} />
                Risolto
              </span>
            )}
            {status === "In revisione" && (
              <span className="pill pill-info">
                <I.Eye size={10} />
                In revisione
              </span>
            )}
            {status === "Non risolto" && <span className="pill pill-err">Non risolto</span>}
            {itemFlags(item).length > 0 && (
              <span
                data-testid={`item-flag-count-${item.id}`}
                className="muted"
                style={{ fontSize: 11, whiteSpace: "nowrap" }}
              >
                {itemFlags(item).length === 1
                  ? "1 segnalazione"
                  : `${itemFlags(item).length} segnalazioni`}
              </span>
            )}
            <CheckStateBadge item={item} />
          </div>
        </td>
        <td className="t-num" style={{ textAlign: "right" }}>
          {/* Goal 55A, D19: a value nobody read is «—», never 0. */}
          {item.qty == null ? "—" : item.qty.toLocaleString("it-IT")}{" "}
          <span className="muted" style={{ fontSize: 11 }}>
            {item.unit}
          </span>
        </td>
        <td className="t-num" style={{ textAlign: "right" }}>
          {item.unitPrice == null
            ? "—"
            : `€ ${item.unitPrice.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`}
        </td>
        <td className="t-num" style={{ textAlign: "right", fontWeight: 500 }}>
          {item.total == null ? "—" : fmtEur(item.total)}
        </td>
        <td />
        <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-3)" }}>
          {item.pctBudget != null ? `${item.pctBudget.toFixed(2)}%` : "—"}
        </td>
        <td>
          {hasD && (
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                openDiscrepancy(item);
              }}
            >
              <I.Chevron size={13} style={{ transform: isOpen ? "rotate(90deg)" : "none" }} />
            </button>
          )}
        </td>
      </tr>

      {isOpen && item.discrepancy && (
        <tr>
          <td
            colSpan={9}
            style={{
              padding: 0,
              background: "var(--c-warn-soft)",
              borderTop: "1px solid rgba(184,115,12,0.20)",
              borderBottom: "1px solid rgba(184,115,12,0.20)",
            }}
          >
            {/* Goal 55A, D17: EVERY flag of the item, most severe first. */}
            <div data-testid={`item-flags-${item.id}`}>
              {itemFlags(item).map((flag, k) => (
                <div
                  key={flag.id}
                  style={k > 0 ? { borderTop: "1px solid rgba(184,115,12,0.20)" } : undefined}
                >
                  <DiscrepancyContent item={{ ...item, discrepancy: flag }} projectId={projectId} />
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

interface UnifiedTableProps extends SharedRowProps {
  categories: AuditCategory[];
  lineItems: AuditLineItem[];
  loading: boolean;
  error: string | null;
  expandedCats: Set<string>;
  toggleCat: (id: string) => void;
  /** Latest analyze job for the project (null = never analyzed) — drives the
   * empty state: live progress while it runs, failure + retry, or a start CTA. */
  analysisJob?: AnalysisJob | null;
  /**
   * `P-63` — go to the preparation screen. NOT "start an analysis".
   *
   * It replaces `onStartAnalysis` + `startPending` + `startError` +
   * `startDisabledReason`, all four of which existed because this page used to
   * start a paid run itself. It no longer can, so none of them has anything to
   * describe — and `startDisabledReason`'s own docstring recorded exactly why
   * the old arrangement was uncomfortable: *"this is the ONLY way back into a
   * project whose owner uploaded documents and left before pressing Avvia,
   * because the intake wizard's state is not persisted."* `P-63` persisted it.
   * The reason the button existed is the thing that got fixed.
   *
   * The role check that lived here goes with it: the preparation screen already
   * refuses a start with a reason, in one place, so this is a plain navigation
   * anyone may perform.
   */
  onOpenPreparation?: () => void;
  /** Pareto view for the "Voci principali" tab (Omar, 2026-07-28). Owned by the
   * page so it shares the page's project resolution; rendered here so the two
   * views sit under one tab bar. */
  pareto?: TopLineItemsData;
  paretoLoading?: boolean;
  paretoError?: string | null;
  /** Opens the "Gestisci categorie" drawer. Only meaningful on the tree view —
   * the button is hidden elsewhere rather than shown doing nothing. */
  onManageCategories?: () => void;
  managingCategories?: boolean;
  /** The drawer itself, owned by the page (it needs the page's mutations) but
   * rendered here so it sits directly under the button that opens it. */
  managePanel?: JSX.Element | null;
}

const SEVERITY_LABEL: Record<Severity, string> = {
  all: "Tutte le severità",
  err: "Solo critiche",
  warn: "Solo da rivedere",
};

export function UnifiedTable({
  categories,
  lineItems,
  loading,
  error,
  expandedCats,
  toggleCat,
  analysisJob,
  onOpenPreparation,
  pareto,
  paretoLoading,
  paretoError,
  onManageCategories,
  managingCategories,
  managePanel,
  ...rowProps
}: UnifiedTableProps): JSX.Element {
  const [view, setView] = useState<View>("tree");
  const [severity, setSeverity] = useState<Severity>("all");

  const cats = categories.filter((c) => !c.isUncategorized);
  const discrepancyCount = lineItems.filter((i) => i.discrepancy != null).length;
  // Goal 55B, L-1: an amount nobody read arrives as null — it is counted, never added as 0.
  const grandUnread = lineItems.filter((i) => i.total == null).length;
  const grandRead = lineItems.length - grandUnread;
  const grandTotal =
    grandUnread && !grandRead
      ? null
      : lineItems.reduce((s, i) => (i.total == null ? s : s + i.total), 0);

  const matchesSeverity = (item: AuditLineItem): boolean =>
    severity === "all" ? true : item.discrepancy?.severity === severity;

  /** ids that belong to a top-level category (itself + its subcategories). */
  const catIdSet = (cat: AuditCategory): Set<string> =>
    new Set<string>([cat.id, ...cat.subs.map((s) => s.id)]);

  const flatItems =
    view === "discrepancies"
      ? lineItems.filter((i) => i.discrepancy != null && matchesSeverity(i))
      : lineItems.filter(matchesSeverity);

  const colSpan = 9;

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
        <Tab active={view === "tree"} onClick={() => setView("tree")}>
          Categorie &amp; voci
        </Tab>
        <Tab active={view === "all"} onClick={() => setView("all")}>
          Tutte le voci ({lineItems.length.toLocaleString("it-IT")})
        </Tab>
        <Tab active={view === "discrepancies"} onClick={() => setView("discrepancies")}>
          Solo discrepanze ({discrepancyCount})
        </Tab>
        <Tab active={view === "pareto"} onClick={() => setView("pareto")}>
          Voci principali{pareto && pareto.coveringCount > 0 ? ` (${pareto.coveringCount})` : ""}
        </Tab>
        <div style={{ flex: 1 }} />
        {view === "tree" && onManageCategories && (
          <div style={{ display: "flex", alignItems: "center", padding: "0 4px 0 14px" }}>
            <button
              className="btn btn-sm"
              onClick={onManageCategories}
              aria-expanded={!!managingCategories}
            >
              <I.Settings size={13} /> Gestisci categorie
            </button>
          </div>
        )}
        {/* The severity filter has no meaning in the Pareto view — showing an
            inert control is worse than showing none. */}
        <div
          style={{
            display: view === "pareto" ? "none" : "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 14px",
          }}
        >
          <DropdownMenu
            align="right"
            width={190}
            header="Filtra per severità"
            items={(["all", "err", "warn"] as Severity[]).map((s) => ({
              label: SEVERITY_LABEL[s],
              active: severity === s,
              onClick: () => setSeverity(s),
            }))}
            renderTrigger={(toggle) => (
              <button className="btn btn-ghost btn-sm" onClick={toggle}>
                <I.Filter size={12} /> Filtra{severity !== "all" ? " (1)" : ""}
              </button>
            )}
          />
        </div>
      </div>

      {managePanel}

      {view === "pareto" ? (
        <TopLineItems
          data={pareto}
          /* Hold the "loading" state until BOTH inputs are known. `hasLineItems`
             comes from a DIFFERENT query than `pareto`, and nothing orders the
             two — so a pareto result arriving first made an analysed project look
             never-analysed for as long as /line-items took to land (plausibly a
             while: it returns every row, this returns a computed top-N). The
             message was transient and self-correcting, but it told the user to
             start an hours-long paid job. Found by the independent tester. */
          loading={!!paretoLoading || loading}
          error={paretoError ?? null}
          hasLineItems={lineItems.length > 0}
          readItems={grandRead}
          itemPages={rowProps.itemPages}
        />
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th style={{ width: 90 }}>ID</th>
              <th>Descrizione</th>
              <th style={{ textAlign: "right", width: 130 }}>Quantità</th>
              <th style={{ textAlign: "right", width: 110 }}>€ unitario</th>
              <th style={{ textAlign: "right", width: 130 }}>Totale</th>
              <th style={{ textAlign: "right", width: 90 }}>€/m²</th>
              <th style={{ textAlign: "right", width: 100 }}>% budget</th>
              <th style={{ width: 38 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={colSpan}
                  style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}
                >
                  Caricamento…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td
                  colSpan={colSpan}
                  style={{ textAlign: "center", padding: 24, color: "var(--c-err)" }}
                >
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && lineItems.length === 0 && (
              <tr>
                <td colSpan={colSpan} style={{ textAlign: "center", padding: 32 }}>
                  {analysisJob && ["queued", "running"].includes(analysisJob.status) ? (
                    // A run is in flight (rediscovered even after reload/reconnect).
                    <div style={{ color: "var(--c-ink-2)" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                        Analisi in corso… {analysisJob.progress}%
                      </div>
                      <div
                        style={{
                          fontSize: 12.5,
                          color: "var(--c-ink-3)",
                          maxWidth: 440,
                          margin: "0 auto",
                        }}
                      >
                        Tempo stimato: 1–3 ore. L&apos;analisi gira sul server: puoi chiudere la
                        pagina o spegnere il computer — i risultati appariranno qui appena pronta.
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* `P-63`, 2026-09-02 — THE PRE-ANALYSIS BRANCH IS GONE, AND SO IS
                          THE START BUTTON.

                          Omar: *"There should no longer be a pre-analysis Audit page
                          from which the user can press Avvia… I want that pre-analysis
                          Audit state to disappear entirely from the user flow."*
                          `AuditPage` now redirects any project without an analysis to
                          `/projects/:id/prepare`, so «Questo progetto non è ancora stato
                          analizzato» and its «Avvia analisi AI» are unreachable and are
                          deleted rather than left as dead markup — an unreachable branch
                          is one a later edit can silently make reachable again.

                          `failed` goes with them: a failed run leaves the project in
                          `ingest` (`runner.py` only ever writes `audit` on success), so
                          it redirects too, and the preparation screen's own Fase 3
                          already carries the failure text and the retry.

                          What survives is the one state that genuinely reaches this
                          page: a run that COMPLETED and extracted nothing. It moves the
                          project to `audit`, so there is no redirect — and its button
                          now NAVIGATES to the preparation screen instead of buying a
                          second 1–3 hour run from a page that shows none of the
                          documents about to be analysed. Omar, 2026-09-02: *"this should
                          also take the user back to the preparation screen rather than
                          starting another paid run directly… The preparation screen
                          should remain the single place from which an analysis is
                          started."* */}
                      {/* Branching on the JOB, not on the empty table. Caught by
                          the independent designer before this shipped: the first
                          draft of this rewrite said «L'analisi è terminata senza
                          estrarre nessuna voce» for EVERY empty state, which is
                          a claim that a run happened. There is a reachable state
                          where none did — a project archived while still in
                          `ingest` keeps its audit route (`projectHasAnalysis`
                          treats `archived` as analysed, because archiving
                          OVERWRITES the status and cannot say what it was), and
                          Dashboard's «Elimina» has no status guard, so any
                          never-analysed project can be put there in one click.
                          Telling that reader an analysis "finished" is the exact
                          class of false statement `P-32` and the reconciliation
                          banners exist to prevent. */}
                      {analysisJob?.status === "completed" ? (
                        <>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                            L&apos;analisi è terminata senza estrarre nessuna voce
                          </div>
                          <div
                            style={{
                              fontSize: 12.5,
                              color: "var(--c-ink-3)",
                              maxWidth: 480,
                              margin: "0 auto 10px",
                            }}
                          >
                            Il computo potrebbe non essere stato riconosciuto. Controlla i documenti
                            del progetto e riprova: una nuova analisi dura 1–3 ore.
                          </div>
                          {onOpenPreparation && (
                            <button className="btn btn-primary btn-sm" onClick={onOpenPreparation}>
                              Controlla i documenti e riprova
                            </button>
                          )}
                        </>
                      ) : analysisJob?.status === "failed" ? (
                        <>
                          {/* Unreachable from `/audit/:id` today — a failed run
                              leaves the project in `ingest`, so the page
                              redirects to the preparation screen, which carries
                              the failure and the retry. Kept, and kept HONEST,
                              because this is a component: it must not describe a
                              failed run as "no analysis" for whoever renders it
                              next. */}
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              marginBottom: 6,
                              color: "var(--c-err)",
                            }}
                          >
                            {/* Goal 55B — a self-contained server sentence is shown as it
                                is (`lib/jobFailure.ts`). */}
                            {failureLine("L'ultima analisi non è riuscita", analysisJob.error)}
                          </div>
                          {onOpenPreparation && (
                            <button className="btn btn-primary btn-sm" onClick={onOpenPreparation}>
                              Controlla i documenti e riprova
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          Non c&apos;è ancora un&apos;analisi da mostrare per questo progetto
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            )}

            {!loading && !error && lineItems.length > 0 && view === "tree" ? (
              <>
                {cats.map((cat) => {
                  const isOpen = expandedCats.has(cat.id);
                  const ids = catIdSet(cat);
                  const itemsInCat = lineItems
                    .filter((t) => t.categoryId != null && ids.has(t.categoryId))
                    .filter(matchesSeverity);
                  return (
                    <Fragment key={cat.id}>
                      {/* Category row */}
                      <tr
                        style={{ background: "var(--c-bg-sunken)", cursor: "pointer" }}
                        onClick={() => toggleCat(cat.id)}
                      >
                        <td>
                          <I.Chevron
                            size={12}
                            style={{
                              color: "var(--c-ink-3)",
                              transform: isOpen ? "rotate(90deg)" : "none",
                              transition: "transform var(--tr-fast)",
                            }}
                          />
                        </td>
                        <td className="t-mono" style={{ color: "var(--c-ink-3)", fontWeight: 600 }}>
                          {cat.code ?? ""}
                        </td>
                        <td style={{ fontWeight: 600, fontSize: 13 }}>{cat.name}</td>
                        <td />
                        <td />
                        <td
                          className="t-num"
                          style={{
                            textAlign: "right",
                            fontWeight: 600,
                            fontFamily: "var(--font-mono)",
                          }}
                        >
                          <KnownTotal cost={cat.cost} unread={cat.unreadAmounts ?? 0} />
                        </td>
                        <td
                          className="t-num"
                          style={{ textAlign: "right", color: "var(--c-ink-2)" }}
                        >
                          {cat.eurM2 != null
                            ? `€ ${cat.eurM2.toFixed(0)}`
                            : cat.unreadAmounts
                              ? NOT_DETERMINABLE
                              : "—"}
                        </td>
                        <td className="t-num" style={{ textAlign: "right" }}>
                          {cat.pct != null
                            ? `${cat.pct.toFixed(1)}%`
                            : grandUnread
                              ? NOT_DETERMINABLE
                              : "—"}
                        </td>
                        <td />
                      </tr>

                      {/* Subcategories */}
                      {isOpen &&
                        cat.subs.map((sub) => (
                          <tr key={sub.id}>
                            <td />
                            <td
                              className="t-mono"
                              style={{ color: "var(--c-ink-3)", fontSize: 11.5 }}
                            >
                              {sub.code ?? ""}
                            </td>
                            <td style={{ color: "var(--c-ink-2)", paddingLeft: 24 }}>
                              ↳ {sub.name}
                            </td>
                            <td />
                            <td />
                            <td className="t-num" style={{ textAlign: "right" }}>
                              <KnownTotal cost={sub.cost} unread={sub.unreadAmounts ?? 0} />
                            </td>
                            <td
                              className="t-num"
                              style={{ textAlign: "right", color: "var(--c-ink-3)" }}
                            >
                              {sub.eurM2 != null
                                ? `€ ${sub.eurM2.toFixed(0)}`
                                : sub.unreadAmounts
                                  ? NOT_DETERMINABLE
                                  : "—"}
                            </td>
                            <td
                              className="t-num"
                              style={{ textAlign: "right", color: "var(--c-ink-3)" }}
                            >
                              {sub.pct != null
                                ? `${sub.pct.toFixed(1)}%`
                                : grandUnread
                                  ? NOT_DETERMINABLE
                                  : "—"}
                            </td>
                            <td />
                          </tr>
                        ))}

                      {/* Line items in this category */}
                      {isOpen && itemsInCat.length > 0 && (
                        <tr>
                          <td
                            colSpan={colSpan}
                            style={{
                              background: "var(--c-bg)",
                              padding: "6px 14px 6px 50px",
                              fontSize: 10.5,
                              color: "var(--c-ink-3)",
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              fontWeight: 600,
                              borderBottom: "1px solid var(--c-line)",
                            }}
                          >
                            Voci in {cat.name}
                          </td>
                        </tr>
                      )}
                      {isOpen &&
                        itemsInCat.map((item) => (
                          <LineItemRow key={item.id} item={item} {...rowProps} />
                        ))}
                    </Fragment>
                  );
                })}

                {/* Totals */}
                <tr style={{ background: "var(--c-bg-sunken)" }}>
                  <td />
                  <td />
                  <td style={{ fontWeight: 600 }}>Totale CME</td>
                  <td />
                  <td />
                  <td
                    className="t-num"
                    style={{
                      textAlign: "right",
                      fontWeight: 700,
                      fontFamily: "var(--font-mono)",
                      fontSize: 14,
                    }}
                  >
                    <KnownTotal cost={grandTotal} unread={grandUnread} />
                  </td>
                  <td />
                  <td className="t-num" style={{ textAlign: "right", fontWeight: 600 }}>
                    {/* The total of the read amounts is not 100% of the bill. */}
                    {grandUnread ? NOT_DETERMINABLE : "100%"}
                  </td>
                  <td />
                </tr>
              </>
            ) : !loading && !error && lineItems.length > 0 ? (
              <>
                {flatItems.map((item) => (
                  <LineItemRow key={item.id} item={item} {...rowProps} />
                ))}
                {flatItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={colSpan}
                      style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}
                    >
                      Nessuna voce corrisponde al filtro.
                    </td>
                  </tr>
                )}
              </>
            ) : null}
          </tbody>
        </table>
      )}
    </div>
  );
}
